import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

import type { MessageContextWithFeatureId } from '@post-office/browser--townsquare/common/types';

const HOME_APP_CHANGEBOARDING_POPUPS_MESSAGE_TEMPLATE_ID = 'home-app-changeboarding-popups';

export type PlacementRenderProps = {
	// Placement -> message props
	dismiss: () => void;

	// App -> Placement -> message props
	userName?: string;
	openRovoPanel?: () => void;
	toggleSidebar?: () => void;
	toggleSwitcher?: (toggle: 'open' | 'close') => void;
	isRovoChatOpen?: boolean;
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
} & EligibleMessage;

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

// Always make new props optional
type AppProps = {
	userName?: string;
	openRovoPanel?: () => void;
	toggleSidebar?: () => void;
	toggleSwitcher?: (toggle: 'open' | 'close') => void;
	isRovoChatOpen?: boolean;
	queryParams?: Record<string, string>;
	children?: (dismiss: VoidFunction) => React.ReactNode;
	featureId?: string;
	eligibleMessage?: EligibleMessage<Record<string, never>>;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'townsquare-screen-space-flags',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
	},
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AppProps
> = createFrontend<AppProps>((Messages) => (appProps) => {
	const { queryParams, featureId, children, eligibleMessage, ...restProps } = appProps;
	const providedMessage =
		FeatureGates.checkGate('townsquare_enable_messages_as_placement_props') && eligibleMessage
			? (eligibleMessage as EligibleMessage<Record<string, never>>)
			: undefined;

	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>(
		providedMessage ? [providedMessage] : [],
	);

	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
	}>('/', {
		queryParams,
		autoRefetch: providedMessage ? { isEnabled: false, updateKey: '' } : undefined,
	});

	useEffect(() => {
		if (providedMessage || isLoading || error) {
			return;
		}

		setMessages(data.messages);
	}, [providedMessage, data, error, isLoading]);

	const handleDismiss = (messageInstanceId: string): void => {
		setMessages((prevMessages) => {
			const filteredMessages = prevMessages.filter(
				(message) => messageInstanceId !== message.messageInstanceId,
			);

			return filteredMessages;
		});
	};

	if ((!providedMessage && (error || isLoading)) || messages.length === 0) {
		return null;
	}

	// Render the first message this instance owns: changeboarding popups only in the instance with a
	// matching featureId, everything else only in the global (no featureId) instance. Skipping instead
	// of stopping at messages[0] stops a changeboarding popup from blocking the messages behind it.
	const messageToRender = providedMessage
		? messages[0]
		: messages.find((message) =>
				message.messageTemplateId === HOME_APP_CHANGEBOARDING_POPUPS_MESSAGE_TEMPLATE_ID
					? featureId !== undefined &&
						featureId === (message as MessageContextWithFeatureId).featureId
					: !featureId,
			);

	if (!messageToRender) {
		return null;
	}

	const childrenFunction = children || (() => null);

	return (
		<Messages.render
			key={messageToRender.messageInstanceId}
			dismiss={() => handleDismiss(messageToRender.messageInstanceId)}
			{...messageToRender}
			{...restProps}
		>
			{childrenFunction}
		</Messages.render>
	);
});

export type PlacementFrontendProps = AppProps;
